import Localization from '@localization/Index'
import { forwardRef, useMemo, useRef, useState } from 'react'
import Form from 'react-bootstrap/esm/Form'
import FormLabel from 'react-bootstrap/esm/FormLabel'
import Select, { OptionsOrGroups, GroupBase, MenuPlacement, StylesConfig, SelectInstance, ThemeConfig, CSSObjectWithLabel } from 'react-select'
import AsyncSelect from 'react-select/async'
import CreatableSelect from 'react-select/creatable'
import AsyncCreatableSelect from 'react-select/async-creatable'
import { SelectComponents } from 'react-select/dist/declarations/src/components'
import CSLabel from './CSLabel'
import { checkResponseStatus } from '@api/Utils'
import debounce from 'debounce-promise'
import { useMobileMediaQuery } from '@utils/MediaComponents'

type ClientProps = {
    client: true
    server?: undefined
    options: OptionsOrGroups<unknown, GroupBase<unknown>> | undefined,
}

type ServerProps = {
    server: true
    client?: undefined,
    loadOptions: Function,
}

type Props =  {
    controlId: string,
    label?: string | JSX.Element,
    mandatory?: boolean,
    hasError?: boolean,
    errorText?: string,
    multi?: boolean,
    width?: number,
    onChange?: Function,
    value?: CSOption | ReadonlyArray<CSOption> | null,
    defaultValue?: CSOption | ReadonlyArray<CSOption> | null,
    isOptionDisabled?: (option:unknown)=>boolean,
    placeholder?: string,
    isClearable?: boolean,
    readonly?: boolean,
    disabled?: boolean,
    menuPlacement?: MenuPlacement
    data_cy?: string,
    styles?: StylesConfig<unknown, boolean, GroupBase<unknown>>
    components?: Partial<SelectComponents<unknown, boolean, GroupBase<unknown>>>
    className?: string
    isCreatable?: boolean
    helpInline?: JSX.Element
    tabIndex?: number
    searchDelay?: number
    minCharLength?: number

} & (
    | ServerProps
    | ClientProps
)

export interface CSOption {
    value: string,
    label: string,
    isDisabled?: boolean,
    additional_info?: {
        [key:string]: unknown
    }
}



const CSReactSelect = forwardRef<SelectInstance, Props>((props:Props, ref:any) =>{

    const { searchDelay=500, mandatory=false, className, server, client, label, errorText, hasError, multi, controlId, width, onChange, value, defaultValue, placeholder=Localization.REACT_SELECT.PLACEHOLDER, isClearable=false, data_cy, styles, components, readonly=false, menuPlacement='auto', isOptionDisabled=()=>false, disabled=false, isCreatable=false, helpInline, tabIndex, minCharLength } = props
    const { options } = props as ClientProps
    const { loadOptions } = props as ServerProps
    const [inputValue, setInputValue] = useState<string>();
    const _internalRef = useRef<any>(null)
    const internalRef = ref ?? _internalRef
    const isMobile = useMobileMediaQuery()


    const debouncedLoadOptions = useMemo(()=>debounce(async (inputValue:string) =>{
        try{
            const results = await loadOptions(inputValue)
            return results
        } catch(error: any) {
            checkResponseStatus(error)
            return []
        }
    }, searchDelay), [loadOptions])

    const readonlyValue = defaultValue?(multi?(defaultValue as CSOption[]).map(option=>option.label).join(', '):(defaultValue as CSOption).label): <>&nbsp;</>

    const theme:ThemeConfig=(theme) => ({
        ...theme,
        borderRadius: 10,
        colors: {
            ...theme.colors,
            primary25: '#f9f9f9',
            primary: '#c4cada',
            dangerLight: theme.colors.neutral10,
            danger: theme.colors.primary
        },
    })

    const selectStyles:StylesConfig = {
        ...styles,
        menu: (baseStyle) =>{
            return {
                ...baseStyle,
                zIndex: 100
            }
        },
        input: (baseStyles) => (isMobile?{
            ...baseStyles,
            fontSize: "16px"
        }: baseStyles),
        control:(baseStyles) => {
        const appliedStyles:CSSObjectWithLabel = {
            ...baseStyles,
            minHeight: '45px',
            paddingLeft: '10px'
        }
        if(hasError) {
            appliedStyles.borderColor = '#dc3545'
        }

        return appliedStyles

    }}

    const SelectComponent = isCreatable?CreatableSelect:Select
    const AsyncSelectComponent = isCreatable?AsyncCreatableSelect: AsyncSelect

    const handleKeyDown = (event: React.KeyboardEvent) => {
        if (!inputValue || !isCreatable) return

        if (event.key === 'Enter' || event.key === 'Tab') {
            event.preventDefault()
            onChange?.({label: inputValue, value: inputValue})
            internalRef?.current?.blur()
            if(multi) {
                internalRef?.current?.focus() 
            }
        }
    }

    return (
        <Form.Group className={className} controlId={controlId} style={{position: 'relative', width: !!width?`${width}px`:'100%'}} data-cy={`select-${data_cy}`}>
            {label && <CSLabel mandatory={mandatory} label={label} helpInline={helpInline} />}
            {client && !readonly && <SelectComponent
                ref={internalRef}
                menuPlacement={menuPlacement}
                onChange={(newValue)=>{
                    onChange?.(newValue ?? undefined)
                }}
                onKeyDown={handleKeyDown}
                onInputChange={(input:string)=>setInputValue(input)}
                value={!value?null:value}
                defaultValue={defaultValue}
                isOptionDisabled={(option:unknown)=>{return isOptionDisabled(option)}}
                id={`CSSelect_${controlId}`}
                inputId={controlId}
                options={options} 
                noOptionsMessage={()=>{
                    if(minCharLength && (inputValue ?? "").length < minCharLength) {
                        return Localization.REACT_SELECT.DIGITARE_CARATTERI_RICERCA(minCharLength)
                    }
                    return Localization.REACT_SELECT.NESSUN_RISULTATO
                }}
                loadingMessage={()=>{return Localization.REACT_SELECT.RICERCA_IN_CORSO}}
                isMulti={multi}
                isDisabled={disabled}
                data-cy={`select-${data_cy}`} 
                placeholder={placeholder} 
                isClearable={isClearable}
                theme={theme}
                styles={selectStyles}
                formatCreateLabel={(value)=>value}
                components={components}
                tabIndex={tabIndex}
                createOptionPosition="first"
                />
            }
            {server && !readonly && <AsyncSelectComponent 
                        ref={internalRef}
                        onChange={(newValue)=>onChange?.(newValue ?? undefined)}
                        onKeyDown={handleKeyDown}
                        onInputChange={(input:string)=>setInputValue(input)}
                        value={!value?null:value}
                        defaultValue={defaultValue}
                        isOptionDisabled={(option:unknown)=>{return isOptionDisabled(option)}}
                        id={`CSSelect_${controlId}`}
                        inputId={controlId}
                        isMulti={multi}
                        isDisabled={disabled}
                        placeholder={placeholder} 
                        loadOptions={debouncedLoadOptions}
                        noOptionsMessage={()=>{
                            if(minCharLength && (inputValue ?? "").length < minCharLength) {
                                return Localization.REACT_SELECT.DIGITARE_CARATTERI_RICERCA(minCharLength)
                            }
                            return Localization.REACT_SELECT.NESSUN_RISULTATO
                        }}
                        loadingMessage={()=>{return Localization.REACT_SELECT.RICERCA_IN_CORSO}}
                        isClearable={isClearable}
                        theme={theme}
                        styles={selectStyles}
                        formatCreateLabel={(value)=>value}
                        components={components}
                        tabIndex={tabIndex}
                        allowCreateWhileLoading
                        createOptionPosition="first"
                    />
            }
            {readonly &&<div style={{width}}><p className="text-muted"><strong>{readonlyValue}</strong></p></div>}
            <FormLabel className='cs-text text-danger' style={{position: 'absolute', bottom:'-25px'}} visuallyHidden={!hasError}>
                <small>{errorText}</small>
            </FormLabel>
        </Form.Group>
        
    )
})

export default CSReactSelect